/**
 * usePendingRewards — fetch & claim pending milestone rewards.
 */
import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";

export interface PendingReward {
  id: string;
  user_id: string;
  action_type: string;
  points: number;
  reference_id: string | null;
  description: string | null;
  created_at: string;
  claimed_at: string | null;
}

export function usePendingRewards() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ["pendingRewards", user?.id],
    enabled: !!user?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<PendingReward[]> => {
      const { data, error } = await supabase
        .from("pending_rewards")
        .select("*")
        .eq("user_id", user!.id)
        .is("claimed_at", null)
        .order("created_at", { ascending: false });
      if (error) {
        logger.warn("pending_rewards fetch error", error);
        return [];
      }
      return (data ?? []) as PendingReward[];
    },
  });

  // Map milestone action_type → achievement id (for the Achievements grid)
  const ACHIEVEMENT_MAP: Record<string, string> = {
    create_first_goal: "first_goal",
    first_connection: "first_connection",
    generate_certificate: "first_certificate",
    first_gift_sent: "first_pledge",
    daily_streak_3: "streak_3",
    daily_streak_7: "streak_7",
    daily_streak_30: "streak_30",
  };

  const claimMutation = useMutation({
    mutationFn: async (rewardId: string) => {
      // Look up the action before claiming so we can mirror to user_achievements
      const reward = (query.data ?? []).find((r) => r.id === rewardId);
      const { data, error } = await supabase.rpc("claim_pending_reward", {
        p_reward_id: rewardId,
      });
      if (error) throw error;
      const result = data as {
        success: boolean;
        error?: string;
        points?: number;
        award?: { total_points: number; level: number; level_up: boolean };
      };
      // Mirror to user_achievements via server RPC (idempotent; silent on errors)
      if (result?.success && reward && user?.id) {
        const achievementId = ACHIEVEMENT_MAP[reward.action_type];
        if (achievementId) {
          await supabase
            .rpc("award_achievement", { p_achievement_type: achievementId })
            .then(() => {}, () => {});
        }
      }
      return result;
    },
    onSuccess: (result) => {
      if (!result?.success) {
        toast({
          title: "Couldn't claim reward",
          description: result?.error ?? "Please try again",
          variant: "destructive",
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["pendingRewards", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["userPoints", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["achievements", user?.id] });
      // Broadcast so every mounted usePoints() updates its chip live.
      try {
        window.dispatchEvent(new CustomEvent("points-awarded", {
          detail: { amount: result.points ?? 0, milestone: !!result.award?.level_up },
        }));
        window.dispatchEvent(new CustomEvent("points-refresh"));
      } catch { /* non-fatal */ }
    },
    onError: (e: Error) => {
      toast({ title: "Claim failed", description: e.message, variant: "destructive" });
    },
  });

  const claim = useCallback(
    (rewardId: string) => claimMutation.mutateAsync(rewardId),
    [claimMutation]
  );

  return {
    rewards: query.data ?? [],
    count: (query.data ?? []).length,
    isLoading: query.isLoading,
    claim,
    isClaiming: claimMutation.isPending,
  };
}
